import { Component, OnInit, OnDestroy, Input, Directive } from '@angular/core';
import { Subscription } from 'rxjs';

import { SyncStatus } from '../data/reducer';

/**
 * Base class for sync button components.
 * Provides common template, styles, and interface for sync buttons.
 *
 * Subclasses must implement:
 * - setupSubscription(): Set up the subscription to sync status
 * - onSyncClick(): Handle the sync button click
 */
@Directive()
export abstract class BaseSyncButtonComponent implements OnInit, OnDestroy {
    @Input() style: 'listItem' | 'button' = 'button';

    /**
     * Override color for the sync status icon. When set, this color is applied to all status icons
     * instead of the default status-specific colors. Useful for buttons with colored backgrounds.
     */
    @Input() iconColor?: string;

    syncStatus: SyncStatus = 'Clean';
    protected subscription: Subscription;

    ngOnInit() {
        this.setupSubscription();
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    /**
     * Subclasses must implement this to set up the subscription to sync status.
     * Set this.subscription to the subscription so it can be cleaned up.
     */
    protected abstract setupSubscription(): void;

    /**
     * Subclasses must implement this to handle the sync button click.
     */
    abstract onSyncClick(): void;

    /**
     * Get the label text for the current sync status.
     * Can be overridden by subclasses for custom labels.
     */
    get label(): string {
        switch (this.syncStatus) {
            case 'Clean':
                return 'Synced';
            case 'Dirty':
                return 'Unsaved changes';
            case 'InProgress':
                return 'Syncing...';
            case 'Error':
                return 'Sync failed';
            default:
                return '';
        }
    }

    protected mapGlobalStatusToSyncStatus(status: 'SYNCING' | 'IDLE' | 'ERROR'): SyncStatus {
        switch (status) {
            case 'SYNCING':
                return 'InProgress';
            case 'ERROR':
                return 'Error';
            case 'IDLE':
            default:
                return 'Clean';
        }
    }
}
